import React from 'react';

import {
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import BlockOutlinedIcon
  from '@mui/icons-material/BlockOutlined';

import CheckCircleOutlineIcon
  from '@mui/icons-material/CheckCircleOutline';

import ClearOutlinedIcon
  from '@mui/icons-material/ClearOutlined';

import InfoOutlinedIcon
  from '@mui/icons-material/InfoOutlined';

import RefreshOutlinedIcon
  from '@mui/icons-material/RefreshOutlined';

import RuleOutlinedIcon
  from '@mui/icons-material/RuleOutlined';

import SearchOutlinedIcon
  from '@mui/icons-material/SearchOutlined';

import VisibilityOutlinedIcon
  from '@mui/icons-material/VisibilityOutlined';

import {
  BieRecordId,
  IBieOnboardingValidationRule,
  IBieValidationRuleSummary,
  IBieValidationSeverityLookup,
  isBieFlagEnabled,
} from './bieonboardingworkbench.models';

import BieOnboardingStatusChip
  from './BieOnboardingStatusChip';


/* ============================================================
 * Types
 * ============================================================
 */

export interface IBieOnboardingRuleGridProps {
  rules: IBieOnboardingValidationRule[];

  /**
   * Rule-level issue counts returned by:
   *
   * workbench.summary.byRule
   */
  issueSummaries?: IBieValidationRuleSummary[];

  severities?: IBieValidationSeverityLookup[];

  loading?: boolean;
  refreshing?: boolean;

  errorMessage?: string;

  selectedRuleId?: BieRecordId | null;

  onRefresh?: () => void;

  onSelectRule?: (
    rule: IBieOnboardingValidationRule,
  ) => void;

  /**
   * Opens the Issues tab filtered to the selected rule.
   */
  onViewRuleIssues?: (
    ruleCode: string,
    rule: IBieOnboardingValidationRule,
  ) => void;
}

interface IBieOnboardingRuleGridState {
  search: string;
  severityCode: string;
  blocking: '' | '0' | '1';
}


/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Presents the configured Pack 5 validation rule catalogue.
 *
 * The grid supports:
 *
 * - Rule-name and code search
 * - Severity filtering
 * - Blocking/non-blocking filtering
 * - Rule execution order
 * - Rule-level issue counts
 * - Navigation to issues generated by a rule
 */
export default class BieOnboardingRuleGrid
  extends React.PureComponent<
    IBieOnboardingRuleGridProps,
    IBieOnboardingRuleGridState
  > {

  public constructor(
    props: IBieOnboardingRuleGridProps,
  ) {
    super(props);

    this.state = {
      search: '',
      severityCode: '',
      blocking: '',
    };
  }


  public render(): React.ReactNode {
    const {
      loading = false,
      refreshing = false,
      errorMessage,
    } = this.props;

    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
        }}
      >
        {refreshing && !loading && (
          <LinearProgress />
        )}

        <Box
          sx={{
            p: 2,
          }}
        >
          {this.renderHeader()}

          <Divider
            sx={{
              my: 2,
            }}
          />

          {this.renderFilters()}
        </Box>

        {errorMessage && (
          <Box
            sx={{
              px: 2,
              pb: 2,
            }}
          >
            <Alert severity="error">
              {errorMessage}
            </Alert>
          </Box>
        )}

        {loading
          ? this.renderLoadingTable()
          : this.renderRuleTable()}
      </Paper>
    );
  }


  /* ==========================================================
   * Header
   * ==========================================================
   */

  private renderHeader(): React.ReactNode {
    const {
      rules,
      issueSummaries = [],
      refreshing = false,
      onRefresh,
    } = this.props;

    const activeRules =
      rules.filter(
        (rule) =>
          Number(rule.sts) === 1,
      ).length;

    const blockingRules =
      rules.filter(
        (rule) =>
          isBieFlagEnabled(
            rule.ibl,
          ),
      ).length;

    const totalIssues =
      issueSummaries.reduce(
        (
          total,
          item,
        ) =>
          total +
          (
            Number(item.count) || 0
          ),
        0,
      );

    return (
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        justifyContent="space-between"
        alignItems={{
          xs: 'stretch',
          sm: 'center',
        }}
        spacing={2}
      >
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <RuleOutlinedIcon
              color="primary"
            />

            <Typography
              variant="h6"
              fontWeight={700}
            >
              Validation Rules
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
            }}
          >
            Review the rules applied to imported workbook
            records during resolution and validation.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
        >
          <Chip
            size="small"
            variant="outlined"
            label={
              `${this.formatNumber(
                activeRules,
              )} active rules`
            }
          />

          <Chip
            size="small"
            color="error"
            variant="outlined"
            icon={
              <BlockOutlinedIcon />
            }
            label={
              `${this.formatNumber(
                blockingRules,
              )} blocking`
            }
          />

          <Chip
            size="small"
            color={
              totalIssues > 0
                ? 'warning'
                : 'success'
            }
            variant="outlined"
            label={
              `${this.formatNumber(
                totalIssues,
              )} current issues`
            }
          />

          {onRefresh && (
            <Tooltip
              title="Refresh validation rules"
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  disabled={refreshing}
                  onClick={onRefresh}
                >
                  <RefreshOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    );
  }


  /* ==========================================================
   * Filters
   * ==========================================================
   */

  private renderFilters(): React.ReactNode {
    const {
      loading = false,
      refreshing = false,
    } = this.props;

    const disabled =
      loading || refreshing;

    return (
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        spacing={1.5}
        alignItems={{
          xs: 'stretch',
          md: 'center',
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="Search validation rules"
          placeholder={
            'Rule code, name or description'
          }
          value={this.state.search}
          disabled={disabled}
          onChange={
            this.handleSearchChange
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon
                  fontSize="small"
                />
              </InputAdornment>
            ),

            endAdornment:
              this.state.search
                ? (
                  <InputAdornment position="end">
                    <Tooltip
                      title="Clear search"
                      arrow
                    >
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={
                          this.handleClearSearch
                        }
                      >
                        <ClearOutlinedIcon
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
                : undefined,
          }}
        />

        <TextField
          select
          size="small"
          label="Severity"
          value={
            this.state.severityCode
          }
          disabled={disabled}
          onChange={
            this.handleSeverityChange
          }
          sx={{
            minWidth: {
              xs: '100%',
              md: 190,
            },
          }}
        >
          <MenuItem value="">
            All Severities
          </MenuItem>

          {this.getSeverityOptions().map(
            (severity) => (
              <MenuItem
                key={severity.code}
                value={severity.code}
              >
                {severity.name}
              </MenuItem>
            ),
          )}
        </TextField>

        <TextField
          select
          size="small"
          label="Impact"
          value={this.state.blocking}
          disabled={disabled}
          onChange={
            this.handleBlockingChange
          }
          sx={{
            minWidth: {
              xs: '100%',
              md: 190,
            },
          }}
        >
          <MenuItem value="">
            All Rules
          </MenuItem>

          <MenuItem value="1">
            Blocking Rules
          </MenuItem>

          <MenuItem value="0">
            Non-Blocking Rules
          </MenuItem>
        </TextField>

        {this.getActiveFilterCount() > 0 && (
          <Tooltip
            title="Clear all rule filters"
            arrow
          >
            <IconButton
              color="inherit"
              disabled={disabled}
              onClick={
                this.handleClearFilters
              }
            >
              <ClearOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  }


  /* ==========================================================
   * Rule table
   * ==========================================================
   */

  private renderRuleTable(): React.ReactNode {
    const rules =
      this.getFilteredRules();

    if (rules.length === 0) {
      return this.renderEmptyState();
    }

    return (
      <TableContainer>
        <Table
          size="small"
          sx={{
            minWidth: 1050,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: 90,
                }}
              >
                Order
              </TableCell>

              <TableCell>
                Rule
              </TableCell>

              <TableCell>
                Severity
              </TableCell>

              <TableCell>
                Impact
              </TableCell>

              <TableCell>
                Description
              </TableCell>

              <TableCell
                align="right"
              >
                Current Issues
              </TableCell>

              <TableCell>
                Status
              </TableCell>

              <TableCell
                align="right"
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rules.map(
              (rule) =>
                this.renderRuleRow(
                  rule,
                ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }


  private renderRuleRow(
    rule: IBieOnboardingValidationRule,
  ): React.ReactNode {
    const selected =
      String(
        this.props.selectedRuleId ?? '',
      ) === String(rule.rid);

    const issueCount =
      this.getRuleIssueCount(
        rule.shc,
      );

    const blocking =
      isBieFlagEnabled(
        rule.ibl,
      );

    return (
      <TableRow
        key={String(rule.rid)}
        hover
        selected={selected}
        onClick={
          () =>
            this.handleSelectRule(
              rule,
            )
        }
        sx={{
          cursor:
            this.props.onSelectRule
              ? 'pointer'
              : 'default',
        }}
      >
        <TableCell>
          <Chip
            size="small"
            variant="outlined"
            label={
              this.formatNumber(
                rule.exo,
              )
            }
            sx={{
              minWidth: 54,
              fontWeight: 700,
            }}
          />
        </TableCell>

        <TableCell>
          <Stack spacing={0.25}>
            <Typography
              variant="body2"
              fontWeight={700}
            >
              {rule.nam || rule.shc}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              fontFamily="monospace"
            >
              {rule.shc}
            </Typography>
          </Stack>
        </TableCell>

        <TableCell>
          <BieOnboardingStatusChip
            kind="SEVERITY"
            value={rule.svc}
            blocking={rule.ibl}
          />
        </TableCell>

        <TableCell>
          {blocking ? (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              icon={
                <BlockOutlinedIcon />
              }
              label="Blocking"
            />
          ) : (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              icon={
                <InfoOutlinedIcon />
              }
              label="Review"
            />
          )}
        </TableCell>

        <TableCell>
          <Tooltip
            title={rule.dsc || ''}
            arrow
          >
            <Typography
              variant="body2"
              color={
                rule.dsc
                  ? 'text.primary'
                  : 'text.disabled'
              }
              sx={{
                maxWidth: 400,

                display:
                  '-webkit-box',

                WebkitLineClamp: 2,
                WebkitBoxOrient:
                  'vertical',

                overflow:
                  'hidden',
              }}
            >
              {
                rule.dsc ||
                'No rule description provided.'
              }
            </Typography>
          </Tooltip>
        </TableCell>

        <TableCell
          align="right"
        >
          <Chip
            size="small"
            color={
              issueCount > 0
                ? blocking
                  ? 'error'
                  : 'warning'
                : 'success'
            }
            variant={
              issueCount > 0
                ? 'filled'
                : 'outlined'
            }
            icon={
              issueCount > 0
                ? <RuleOutlinedIcon />
                : <CheckCircleOutlineIcon />
            }
            label={
              this.formatNumber(
                issueCount,
              )
            }
          />
        </TableCell>

        <TableCell>
          <Chip
            size="small"
            color={
              Number(rule.sts) === 1
                ? 'success'
                : 'default'
            }
            variant="outlined"
            label={
              Number(rule.sts) === 1
                ? 'Active'
                : 'Inactive'
            }
          />
        </TableCell>

        <TableCell
          align="right"
        >
          {this.props.onViewRuleIssues && (
            <Tooltip
              title={
                issueCount > 0
                  ? 'View issues generated by this rule'
                  : 'No current issues generated by this rule'
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  disabled={issueCount <= 0}
                  onClick={
                    (event) =>
                      this.handleViewRuleIssues(
                        event,
                        rule,
                      )
                  }
                >
                  <VisibilityOutlinedIcon
                    fontSize="small"
                  />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    );
  }


  /* ==========================================================
   * Loading and empty states
   * ==========================================================
   */

  private renderLoadingTable(): React.ReactNode {
    return (
      <Box>
        <LinearProgress />

        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: 1050,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  Order
                </TableCell>

                <TableCell>
                  Rule
                </TableCell>

                <TableCell>
                  Severity
                </TableCell>

                <TableCell>
                  Impact
                </TableCell>

                <TableCell>
                  Description
                </TableCell>

                <TableCell
                  align="right"
                >
                  Current Issues
                </TableCell>

                <TableCell>
                  Status
                </TableCell>

                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.from({
                length: 8,
              }).map(
                (_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton
                        variant="rounded"
                        width={48}
                        height={24}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton
                        variant="text"
                        width={190}
                      />

                      <Skeleton
                        variant="text"
                        width={130}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton
                        variant="rounded"
                        width={105}
                        height={28}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton
                        variant="rounded"
                        width={90}
                        height={28}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton
                        variant="text"
                        width={300}
                      />
                    </TableCell>

                    <TableCell
                      align="right"
                    >
                      <Skeleton
                        variant="rounded"
                        width={48}
                        height={28}
                        sx={{
                          ml: 'auto',
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton
                        variant="rounded"
                        width={70}
                        height={28}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton
                        variant="circular"
                        width={28}
                        height={28}
                      />
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }


  private renderEmptyState(): React.ReactNode {
    return (
      <Box
        sx={{
          py: 7,
          px: 3,

          textAlign: 'center',

          borderTop:
            '1px solid',

          borderColor:
            'divider',
        }}
      >
        <RuleOutlinedIcon
          color="disabled"
          sx={{
            fontSize: 48,
          }}
        />

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mt: 1,
          }}
        >
          No validation rules found
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.75,
          }}
        >
          No configured rules match the current search and
          filter criteria.
        </Typography>
      </Box>
    );
  }


  /* ==========================================================
   * Event handlers
   * ==========================================================
   */

  private readonly handleSearchChange =
    (
      event:
        React.ChangeEvent<HTMLInputElement>,
    ): void => {
      this.setState({
        search:
          event.target.value,
      });
    };


  private readonly handleSeverityChange =
    (
      event:
        React.ChangeEvent<HTMLInputElement>,
    ): void => {
      this.setState({
        severityCode:
          event.target.value,
      });
    };


  private readonly handleBlockingChange =
    (
      event:
        React.ChangeEvent<HTMLInputElement>,
    ): void => {
      const value =
        event.target.value;

      this.setState({
        blocking:
          value === '0' ||
            value === '1'
            ? value
            : '',
      });
    };


  private readonly handleClearSearch =
    (): void => {
      this.setState({
        search: '',
      });
    };


  private readonly handleClearFilters =
    (): void => {
      this.setState({
        search: '',
        severityCode: '',
        blocking: '',
      });
    };


  private handleSelectRule(
    rule: IBieOnboardingValidationRule,
  ): void {
    if (!this.props.onSelectRule) {
      return;
    }

    this.props.onSelectRule(
      rule,
    );
  }


  private handleViewRuleIssues(
    event:
      React.MouseEvent<HTMLButtonElement>,
    rule: IBieOnboardingValidationRule,
  ): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.props.onViewRuleIssues) {
      return;
    }

    this.props.onViewRuleIssues(
      rule.shc,
      rule,
    );
  }


  /* ==========================================================
   * Filtering
   * ==========================================================
   */

  private getFilteredRules():
    IBieOnboardingValidationRule[] {
    const search =
      this.state.search
        .trim()
        .toLowerCase();

    const severityCode =
      this.state.severityCode
        .trim()
        .toUpperCase();

    const blocking =
      this.state.blocking;

    return [...this.props.rules]
      .filter(
        (rule) => {
          if (
            severityCode &&
            String(rule.svc)
              .toUpperCase() !==
            severityCode
          ) {
            return false;
          }

          if (blocking !== '') {
            const expectedBlocking =
              blocking === '1';

            if (
              isBieFlagEnabled(
                rule.ibl,
              ) !== expectedBlocking
            ) {
              return false;
            }
          }

          if (!search) {
            return true;
          }

          const searchableText = [
            rule.shc,
            rule.nam,
            rule.dsc,
            rule.svc,
            rule.svn,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return searchableText.includes(
            search,
          );
        },
      )
      .sort(
        (
          first,
          second,
        ) => {
          const orderDifference =
            Number(first.exo) -
            Number(second.exo);

          if (orderDifference !== 0) {
            return orderDifference;
          }

          return String(first.shc)
            .localeCompare(
              String(second.shc),
            );
        },
      );
  }


  private getSeverityOptions(): Array<{
    code: string;
    name: string;
  }> {
    const configured =
      this.props.severities || [];

    if (configured.length > 0) {
      return configured
        .map(
          (severity) => ({
            code:
              severity.code,

            name:
              severity.name,
          }),
        )
        .sort(
          (
            first,
            second,
          ) =>
            first.name.localeCompare(
              second.name,
            ),
        );
    }

    return Array.from(
      new Set(
        this.props.rules
          .map(
            (rule) =>
              String(
                rule.svc || '',
              )
                .trim()
                .toUpperCase(),
          )
          .filter(Boolean),
      ),
    )
      .sort()
      .map(
        (code) => ({
          code,
          name:
            code.charAt(0) +
            code
              .slice(1)
              .toLowerCase(),
        }),
      );
  }


  private getRuleIssueCount(
    ruleCode: string,
  ): number {
    const summary =
      (
        this.props.issueSummaries ||
        []
      ).find(
        (item) =>
          String(item.ruleCode)
            .trim()
            .toUpperCase() ===
          String(ruleCode)
            .trim()
            .toUpperCase(),
      );

    return Number(
      summary?.count || 0,
    );
  }


  private getActiveFilterCount(): number {
    return [
      this.state.search,
      this.state.severityCode,
      this.state.blocking,
    ].filter(
      (value) =>
        String(value)
          .trim() !== '',
    ).length;
  }


  private formatNumber(
    value: unknown,
  ): string {
    const numericValue =
      Number(value);

    if (
      !Number.isFinite(
        numericValue,
      )
    ) {
      return '0';
    }

    return new Intl.NumberFormat()
      .format(numericValue);
  }
}


// Example usage:
// <BieOnboardingRuleGrid
//   rules={this.state.rules}
//   issueSummaries={
//     this.state.workbench
//       ?.summary
//       .byRule || []
//   }
//   severities={
//     this.state.workbench
//       ?.lookups
//       .severities || []
//   }
//   loading={this.state.loading}
//   refreshing={this.state.refreshing}
//   onRefresh={
//     this.loadValidationRules
//   }
//   onViewRuleIssues={
//     this.handleViewRuleIssues
//   }
// />


// A rule can open the Issues tab with the corresponding filter:
// private handleViewRuleIssues = (
//   ruleCode: string,
// ): void => {
//   this.setState(
//     (currentState) => ({
//       selectedTab: 'ISSUES',

//       filters: {
//         ...currentState.filters,
//         ruleCode,
//       },

//       issuePageOffset: 0,
//     }),
//     this.loadValidationIssues,
//   );
// };